import {graphql} from 'gatsby'
import React from 'react'

import {Header} from '@/components/header'
import {Footer} from '@/components/secondary-footer'
import SEO from '@/components/seo'
import {Main} from '@/components/skip-to-content'
import {secondaryLocaleFooters} from '@/hardcoded-locales/footer'
import {secondaryLocaleHeaders} from '@/hardcoded-locales/header'
import locales from '@/locale/locales'
import {NotFound} from '@/sections/404'
import handleNoPrismicData from '@/utils/handle-no-prismic-data'
import {withPrismicPreview} from '@/utils/with-prismic-preview'

import type {SecondaryNotFoundPageQuery} from '@/graphql-types'
import type {GatsbyHeadProps} from '@/types/gatsby-head'
import type {PageProps} from 'gatsby'

type Props = PageProps<SecondaryNotFoundPageQuery>

const SecondaryNotFoundPage = ({data}: Props) => {
    const content = data.prismicSecondaryNotFoundPage.data

    const headerContent =
        secondaryLocaleHeaders[content.locale_code as keyof typeof secondaryLocaleHeaders]
    const footerContent =
        secondaryLocaleFooters[content.locale_code as keyof typeof secondaryLocaleFooters]

    if (!content) {
        return handleNoPrismicData()
    }

    return (
        <>
            <Header genericBookADemo={true} content={headerContent} />

            <Main>
                <NotFound
                    content={{
                        ...content,
                        ctaLink: {
                            // Secondary Homepage needs manual ctaLink casting because linkResolver requires localeCode field value.
                            _meta: {},
                            _linkType: 'Link.document',
                            ...content.cta_link.document,
                        } as any,
                    }}
                />
            </Main>

            <Footer
                hideLinks={false}
                locales={locales}
                content={{...footerContent, address: footerContent.address.richText}}
            />
        </>
    )
}

export const Head = ({
    location,
    pageContext,
    data,
}: GatsbyHeadProps<SecondaryNotFoundPageQuery>) => {
    const pageData = data.prismicSecondaryNotFoundPage.data

    return (
        <SEO
            location={location}
            pageContext={pageContext}
            title={pageData.meta_title}
            description={pageData.meta_description}
            openGraphImageUrl={pageData.open_graph_image?.url}
        />
    )
}

export const query = graphql`
    query SecondaryNotFoundPage($id: String!) {
        prismicSecondaryNotFoundPage(id: {eq: $id}, lang: {eq: "en-ie"}) {
            _previewable
            data {
                locale_code
                meta_title
                meta_description
                open_graph_image {
                    url
                }
                title
                body
                ctaText: cta_text
                cta_link {
                    document {
                        ... on PrismicSecondaryHomepage {
                            type
                            data {
                                localeCode: locale_code
                            }
                        }
                    }
                }
            }
        }
    }
`

export default withPrismicPreview(SecondaryNotFoundPage)
